import {
  ActionCreator,
  ActionType,
  createAction as ngrxCreateAction,
  createReducer as ngrxCreateReducer,
  createSelectorFactory,
  defaultMemoize,
  MemoizedSelector,
  MemoizedSelectorWithProps,
  on as ngrxOn,
  props as ngrxProps,
  SelectorWithProps,
} from '@ngrx/store';

import {
  ActionScopedPayload,
  ActionPayloadFromConfigFn,
  ActionsFromConfig,
  ConfigFn,
  ConfigObject,
  Reference,
} from './types';

type RemoveVoid<T> = T extends void ? never : T;

export const ngrxEvent = <T>(): RemoveVoid<T> extends void
  ? <S>(state: S) => S
  : <S>(state: S, payload: T) => S => <any>((state: any) => state);

const createActions = <State, T extends ConfigObject<any>>(
  namespace: string,
  actions: T,
): ActionsFromConfig<T> => {
  const entries = <[Extract<keyof T, string>, ConfigFn<State>][]>Object.entries(actions);
  return entries.reduce((acc, [actionName, handler]) => {
    const actionCreator = ngrxCreateAction(
      `[${namespace}] ${actionName}`,
      ngrxProps<{ payload: ActionScopedPayload<ActionPayloadFromConfigFn<typeof handler>> }>(),
    );

    // @ts-ignore
    acc[actionName] = Object.assign(actionCreator, { namespace, actionName });

    return acc;
  }, <ActionsFromConfig<T>>{});
};

const createReducer = <S, C extends ConfigObject<S>>(
  state: S,
  config: C,
  actions: ActionsFromConfig<C>,
) => {
  const actionEntries = Object.entries(actions);
  return ngrxCreateReducer(
    state,
    ...actionEntries.map(([key, action]) =>
      ngrxOn(
        action,
        (
          currentState: S,
          actionInstance: ActionType<
            ActionCreator<
              string,
              (options: { payload?: ActionScopedPayload<any> }) => {
                payload?: ActionScopedPayload<any>;
              }
            >
          >,
        ) => config[key](currentState, actionInstance.payload),
      ),
    ),
  );
};

type SelectorWithRef<State extends object, Result, Props, P2 = Props> = Props extends
  | object
  | string
  | number
  ? MemoizedSelectorWithProps<State, P2, Result | null>
  : MemoizedSelector<State, Result | null>;

export const createSelectorWithReferences = <State extends object, Result = any, Props = void>(
  baseStateSelector: MemoizedSelectorWithProps<State, any, Result>,
  referenceSelectors: Reference<any>[] | null,
  resultFn: (state: Result, props?: Props) => Result,
): SelectorWithRef<State, Result, Props> => {
  const referencesConfig = referenceSelectors || [];

  let cacheArgs: any[] | null = null;
  let resultCache: any = null;

  const selectorFactory = createSelectorFactory(defaultMemoize, {
    stateFn: (rootState, selectors, props, memoizedProjector) => {
      const [mainSelector] = <SelectorWithProps<any, any, any>[]>selectors;
      const mainState = mainSelector(rootState, props);
      const isArray = Array.isArray(mainState);
      const mainStateArr = isArray ? mainState : [mainState];

      const cacheArgsTemp: any[] = [props];

      const result = mainStateArr.map((entity: any) => {
        const copy = { ...entity };
        cacheArgsTemp.push(entity);
        for (let i = 0; i < referencesConfig.length; i++) {
          const reference = referencesConfig[i];
          const referenceState = reference.source(rootState, entity[reference.mapFrom]);
          cacheArgsTemp.push(referenceState);
          copy[reference.mapTo] = referenceState;
        }
        return copy;
      });

      const cachedArgsChanged =
        cacheArgs && cacheArgsTemp.length === cacheArgs.length
          ? cacheArgs.some((arg, i) => arg !== cacheArgsTemp[i])
          : true;

      if (cachedArgsChanged) {
        cacheArgs = cacheArgsTemp;
        resultCache = isArray ? result : result[0];
      }

      return memoizedProjector.memoized(resultCache, props);
    },
  });

  return <any>(
    selectorFactory([baseStateSelector, ...referencesConfig.map(({ source }) => source), resultFn])
  );
};

export const createActionsWithReducer = <NS extends string, State, T extends ConfigObject<State>>(
  namespace: NS,
  state: State,
  actionReducerMap: T,
) => {
  const actions = createActions<State, T>(namespace, actionReducerMap);

  return { actions, reducer: createReducer<State, T>(state, actionReducerMap, actions) };
};

export const pipeState = <T>(state: T, ...transformFns: ((s: T) => T)[]): T =>
  transformFns.reduce((previousState, transformFn) => transformFn(previousState), state);
